<template>
  <FetchJson :url="url" @fetched="onDataFetched">
    <InfoBlock>
      <template v-slot:header>Количество по территориям</template>
      <template v-slot:subtitle>
        Количество размещенных в ГИСОГД документов
      </template>
      <template v-slot:default>
        <div :key="componentKey" :style="'height: ' + chartAreaHeight + 'px'">
          <VueApexCharts
            :options="chartOptions"
            :series="seriesToShow"
            height="100%"
          ></VueApexCharts>
        </div>
      </template>
      <template v-slot:footer>
        <button
          class="button button--outline"
          @click="onShowMore"
          v-show="canShowMore"
        >
          {{
            showMore
              ? "Скрыть остальные территории"
              : "Показать остальные территории"
          }}
        </button>
      </template>
    </InfoBlock>
  </FetchJson>
</template>

<script>
import { computed, ref, toRefs } from "vue";
import { dateToString } from "@/modules/date";
import VueApexCharts from "vue3-apexcharts";
import useConstants from "@/hooks/constants";
import {
  extractStackedSeriesData,
  sortStackedSeriesData,
} from "@/modules/chart";

export default {
  components: { VueApexCharts },
  props: {
    filter: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const componentKey = ref(Date.now());
    const filter = toRefs(props.filter);
    const url = computed(
      () =>
        `gisogd/document-distribution-by-territory?from=${dateToString(
          filter.from.value
        )}&to=${dateToString(filter.to.value)}`
    );
    const series = ref([]);
    const isDataEmpty = ref(false);
    const categories = ref([]);
    const isTooltipEnabled = ref(true);
    const fillColors = ref(["#518CFF", "#FCC8C2"]);
    const filterType = ref("lighten");

    const onDataFetched = function (data) {
      data = sortStackedSeriesData(data);
      series.value = data.series;
      categories.value = data.categories;

      const onDataEmpty = function () {
        isDataEmpty.value = true;
        isTooltipEnabled.value = false;
        filterType.value = "none";
        fillColors.value = ["#D5D5D5"];

        for (var i = 0; i < series.value[0].values.length; i++) {
          series.value[0].values[i] = 100;
        }
      };

      const onDataNotEmpty = function () {
        isDataEmpty.value = false;
        isTooltipEnabled.value = true;
        filterType.value = "lighten";
        fillColors.value = ["#518CFF", "#FCC8C2"];
      };

      if (isFetchedDataEmpty()) {
        onDataEmpty();
      } else {
        onDataNotEmpty();
      }

      componentKey.value = Date.now(); // Tell Vue to re-create the component.
    };
    const constants = useConstants().charts;
    const canShowMore = computed(
      () => categories.value.length > constants.itemCountToShow
    );
    const showMore = ref(false);
    const itemCountToShow = computed(() => {
      if (canShowMore.value)
        return showMore.value
          ? categories.value.length
          : constants.itemCountToShow;

      return categories.value.length;
    });
    const seriesToShow = computed(() => {
      const count = itemCountToShow.value;

      return [
        extractStackedSeriesData(series.value, "InTerm", count),
        extractStackedSeriesData(series.value, "Expired", count),
      ];
    });
    function isFetchedDataEmpty() {
      var inTermIsEmpty = series.value[0].values.every((x) => x == 0);
      var expiredIsEmpty = series.value[1].values.every((x) => x == 0);

      if (inTermIsEmpty && expiredIsEmpty) {
        return true;
      }

      return false;
    }
    const categoriesToShow = computed(() =>
      showMore.value
        ? categories.value
        : categories.value.slice(0, constants.itemCountToShow)
    );
    const chartAreaHeight = computed(
      () =>
        constants.barHeight * itemCountToShow.value +
        constants.footerChartHeight
    );
    const chartOptions = computed(() => {
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          animations: {
            enabled: true,
            easing: "easein",
            speed: 200,
            animateGradually: {
              enabled: true,
              delay: 150,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
        },
        tooltip: {
          fixed: {
            enabled: false,
          },
          x: {
            show: false,
          },
          marker: {
            show: false,
          },
          enabled: isTooltipEnabled.value, // скрываем тултип при наведении
          y: {
            title: {
              formatter: () => "Количество документов : ",
            },
          },
        },
        grid: {
          show: false,
        },
        plotOptions: {
          bar: {
            barHeight: "48%",
            horizontal: true,
            dataLabels: {
              position: "top",
              hideOverflowingLabels: false,
            },
          },
        },
        dataLabels: {
          enabledOnSeries: [1],
          style: {
            colors: ["#465362"],
            fontSize: "14px",
            fontWeight: "regular",
          },
          offsetX: 20,
          textAnchor: "start",
          formatter: function (val, opts) {
            var result = val + opts.w.globals.series[0][opts.dataPointIndex];
            if (isDataEmpty.value) return "0";
            return result;
          },
        },
        colors: ["#497DE6", "#FCC8C2"],
        xaxis: {
          categories: categoriesToShow.value,
          labels: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: [],
              fontSize: "16px",
              fontFamily: "Source Sans Pro, Tahoma, sans-serif",
              fontWeight: 400,
              cssClass: "apexcharts-yaxis-label",
            },
            align: "left",
            offsetX: 8,
            maxWidth: 250,
            formatter: (value) =>
              value.length >= 20 ? `${value.substr(0, 19)}...` : value,
          },
        },
        legend: {
          position: "bottom",
          horizontalAlign: "right",
          fontSize: "16px",
          markers: {
            radius: 12,
          },
        },
        fill: {
          colors: fillColors.value, // красим график
        },
        states: {
          hover: {
            filter: {
              type: filterType.value, // график не пропадает при наведении
            },
          },
        },
      };
    });

    return {
      url,
      componentKey,
      chartOptions,
      seriesToShow,
      canShowMore,
      showMore,
      chartAreaHeight,
      onDataFetched,
    };
  },
  created() {
    this.componentKey = Date.now(); // Tell Vue to re-create the component.
  },
  methods: {
    onShowMore() {
      this.showMore = !this.showMore;
      this.componentKey = Date.now(); // Tell Vue to re-create the component.
    },
  },
};
</script>
<style scoped>
:deep(.apexcharts-bar-series.apexcharts-plot-series) {
  transform: scaleX(0.9);
}
</style>
